import ERRORS from "constants/localization/errors";
import INFOS from "constants/localization/infos";
import SUCCESSES from "constants/localization/successes";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { handleFetchClassroom } from "Store/classrooms/actions";
import {
  handleFetchClassroomCalendarLessonsSaga,
  handleFetchClassroomCalendarTestsSaga,
  handleFetchClassroomCalendarWorksheetsSaga
} from "Store/classrooms/saga";
import { makeSelectActiveClassroom } from "Store/classrooms/selectors";
import * as courseTemplatesActions from "Store/courseTemplates/actions";
import { setCourseTemplateLoader, setSelectedCourseTemplate } from "Store/courseTemplates/actions";
import {
  applyCourseTemplateToClassApi,
  applyCourseTemplateToStudentApi,
  createCourseTemplateApi,
  deleteCourseTemplatesApi,
  fetchCourseTemplateApi,
  fetchCourseTemplatesByTypeApi,
  fetchCourseTemplateSessionsApi,
  updateCourseTemplateApi
} from "Store/courseTemplates/api";
import * as courseTemplatesTypes from "Store/courseTemplates/constants";
import * as globalActions from "Store/global/actions";
import { convertMetaToPagination, renderApiParameters } from "Store/global/saga";
import * as globalSelectors from "Store/global/selectors";
import { handleFetchStudentCalendarDataSaga } from "Store/students/saga";
import { Toast } from "utils/toast";

export const getEstimateTime = (problemsCount: number, studentsCount): number =>
  ((problemsCount * studentsCount) / 130) * 1000;

export const getCountOfProblemsFromAssignments = (assignments): number =>
  assignments
    .map((assignment) => assignment.problem_count)
    .reduce((previousValue, currentValue) => previousValue + currentValue, 0);

const getIdsAssignmentsBySession = (
  sessions: Session[],
  isUpdate?: boolean,
  fromCalendar?: boolean,
  classroomTemplate?: boolean
) => {
  const assignmentsName = ["lessons", "worksheets", "sessions", "tests"];
  sessions.map((session) => {
    for (const [key, value] of Object.entries(session)) {
      if (assignmentsName.includes(key)) {
        if (key !== "tests") {
          if (session[key]?.length) {
            if (isUpdate) {
              session[key] = assignmentsName.includes(key) && session[key]?.map((item) => item.id);
            } else {
              if (!classroomTemplate) {
                session[key] = session[key]?.map(
                  (item) => item[`${key.substring(0, key.length - 1)}_id`] || item.id
                );
              } else {
                session[key] = session[key]?.map((item) => item.id);
              }
            }
          }
        } else {
          session[key] = session[key]?.map((item) => {
            if (item.sections?.length) {
              if (!classroomTemplate) {
                return {
                  test_id: fromCalendar ? item.test_id : item.id,
                  test_section_ids: fromCalendar
                    ? item.sections.map((section) => section["section"].id)
                    : item.sections
                };
              } else {
                return {
                  test_id: item.id,
                  test_section_ids: item.sections.map((section) => section.id)
                };
              }
            } else {
              if (item.type != 'PAPER') {
                if (item.type == 'DIGITAL') {
                  return {
                    test_id : item.test_id ? item.test_id : item.id,
                    test_section_ids: item.test_section_ids
                  }
                } else {
                  return {
                    test_id : item.test_id ? item.test_id : item.id,
                    test_section_ids: []
                  }
                }
              }
              return item;
            }
          });
        }
      }
    }
  });
};

function* handleFetchCourseTemplateSessions(action) {
  const { postBody } = action;
  try {
    const { error, data } = yield call(fetchCourseTemplateSessionsApi, postBody);
    if (error) {
      return Toast.error(ERRORS.handleFetchCourseTemplateSessions, error);
    }
    const sessionSchedule = {
      course_template_id: postBody.template_id,
      sessions: data
    };
    yield put(courseTemplatesActions.setCourseTemplateSessions(sessionSchedule));
  } catch (error) {
    return Toast.error(ERRORS.handleFetchCourseTemplateSessions, error);
  }
}

function* handleApplyTemplateToStudent(action) {
  yield put(globalActions.setActionSpinner(true));
  try {
    const { error } = yield applyCourseTemplateToStudentApi(action.payload);
    if (error) {
      Toast.error(ERRORS.handleApplyTemplateToStudent, error);
    } else {
      yield call(handleFetchStudentCalendarDataSaga);
      Toast.success(SUCCESSES.handleApplyTemplateToStudent);
    }
    yield put(globalActions.setActionSpinner(false));
  } catch (error) {
    Toast.error(ERRORS.handleApplyTemplateToStudent, error.message);
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleApplyTemplateToClassroomSaga(action) {
  yield put(globalActions.setActionSpinner(true));
  try {
    const { data } = yield fetchCourseTemplateApi(action.payload.course_template_id);
    const { sessions } = data;
    const lessons = sessions.map((session) => session.lessons).flat(1);
    const worksheets = sessions.map((session) => session.worksheets).flat(1);
    const activeClassroom: Classroom = yield select(makeSelectActiveClassroom);

    const lessonsProblemCount = getCountOfProblemsFromAssignments(lessons);
    const worksheetsProblemCount = getCountOfProblemsFromAssignments(worksheets);

    const estimateTimeAssigningWorksheets = getEstimateTime(
      worksheetsProblemCount,
      activeClassroom.active_students
    );

    const estimateTimeAssigningLessons = getEstimateTime(
      lessonsProblemCount,
      activeClassroom.active_students
    );

    Toast.info(INFOS.handleApplyTemplateToClass, {
      autoClose: estimateTimeAssigningWorksheets + estimateTimeAssigningLessons,
      position: "bottom-left"
    });

    const { error } = yield applyCourseTemplateToClassApi(action.payload);
    yield put(globalActions.setActionSpinner(false));
    if (error) {
      Toast.error(ERRORS.handleApplyTemplateToClass, error);
    } else {
      yield put(handleFetchClassroom(action.payload.classroom_id));
      yield call(handleFetchClassroomCalendarLessonsSaga);
      yield call(handleFetchClassroomCalendarWorksheetsSaga);
      yield call(handleFetchClassroomCalendarTestsSaga);
    }
  } catch (error) {
    Toast.error(ERRORS.handleApplyTemplateToClass, error.message);
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleFetchSelectedCourseTemplateSaga(
  action: courseTemplatesTypes.IHandleFetchSelectedCourseTemplate
) {
  try {
    yield put(globalActions.setFetchDetailsSpinner(true));
    const { data, error } = yield fetchCourseTemplateApi(action.payload);
    if (error) {
      yield put(globalActions.setFetchDetailsSpinner(false));
      return Toast.error(ERRORS.handleFetchSelectedCourseTemplateSaga, error);
    } else {
      yield put(setSelectedCourseTemplate(data));
      yield put(globalActions.setFetchDetailsSpinner(false));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchSelectedCourseTemplateSaga, error);
    yield put(globalActions.setFetchDetailsSpinner(false));
  }
}

//
//
// REWRITED SAGAS
//
//
//

function* handleFetchCourseTemplatesSaga() {
  yield put(setCourseTemplateLoader(true));
  try {
    const parameters = yield renderApiParameters(globalSelectors, "courseTemplates");
    const { error, meta, data } = yield fetchCourseTemplatesByTypeApi(parameters);
    if (error) {
      Toast.error(ERRORS.handleFetchCourseTemplates, error);
    }
    yield put(globalActions.setPagination(convertMetaToPagination(meta), "courseTemplates"));
    yield put(courseTemplatesActions.setCourseTemplate([...data]));
    yield put(setCourseTemplateLoader(false));
  } catch (error) {
    yield put(setCourseTemplateLoader(false));
    Toast.error(ERRORS.handleFetchCourseTemplates, error);
  }
}

function* handleDeleteCourseTemplatesSaga(
  action: courseTemplatesTypes.IHandleDeleteCourseTemplates
) {
  try {
    yield put(globalActions.setActionSpinner(true));
    const { courseTemplatesIds } = action.payload;
    const { error, status } = yield deleteCourseTemplatesApi({
      course_templates: courseTemplatesIds
    });
    if (error) {
      Toast.error(
        ERRORS.handleDeleteCourseTemplates,
        status === 403 ? "You are not authorised to update/delete this course template" : error
      );
    } else {
      Toast.success(SUCCESSES.handleDeleteCourseTemplates);
      yield call(handleFetchCourseTemplatesSaga);
    }
  } catch (error) {
    Toast.error(ERRORS.handleDeleteCourseTemplates, error);
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleCreateCourseTemplateSaga(action: courseTemplatesTypes.IHandleCreateCourseTemplate) {
  try {
    yield put(globalActions.setActionSpinner(true));
    const {
      courseTemplate: { name, description, sessions, is_active },
      fromCalendar,
      classroomTemplate
    } = action.payload;
    const body: CreateCourseTemplateRequest = {
      description,
      name,
      sessions,
      is_active
    };
    getIdsAssignmentsBySession(sessions, false, fromCalendar, classroomTemplate);

    const { error } = yield createCourseTemplateApi(body);
    if (error) {
      return Toast.error(ERRORS.handleCreateCourseTemplate, error);
    }
    Toast.success(SUCCESSES.handleCreateCourseTemplate);
    return yield call(handleFetchCourseTemplatesSaga);
  } catch (error) {
    return Toast.error(ERRORS.handleCreateCourseTemplate, error);
  } finally {
    return yield put(globalActions.setActionSpinner(false));
  }
}

function* handleUpdateCourseTemplateSaga(action: courseTemplatesTypes.IHandleUpdateCourseTemplate) {
  try {
    yield put(globalActions.setActionSpinner(true));
    const {
      courseTemplate: { id, name, description, sessions, is_active }
    } = action.payload;
    const body: UpdateCourseTemplateRequest = {
      course_template_id: id,
      name,
      description,
      sessions,
      is_active
    };
    getIdsAssignmentsBySession(sessions, true);
    const { error, status } = yield updateCourseTemplateApi(body);
    if (error) {
      return Toast.error(
        ERRORS.handleUpdateCourseTemplate,
        status === 403 ? "You are not authorised to update/delete this course template" : error
      );
    }
    Toast.success(SUCCESSES.handleUpdateCourseTemplate);
    return yield call(handleFetchCourseTemplatesSaga);
  } catch (error) {
    return Toast.error(ERRORS.handleUpdateCourseTemplate, error);
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

export default function* courseTemplatesSaga() {
  yield takeLatest(
    courseTemplatesTypes.HANDLE_FETCH_ALL_COURSE_TEMPLATES,
    handleFetchCourseTemplatesSaga
  );
  yield takeLatest(
    courseTemplatesTypes.GET_COURSE_TEMPLATE_SESSIONS,
    handleFetchCourseTemplateSessions
  );
  // =========
  yield takeLatest(
    courseTemplatesTypes.HANDLE_APPLY_TEMPLATE_TO_STUDENT,
    handleApplyTemplateToStudent
  );

  yield takeLatest(
    courseTemplatesTypes.HANDLE_APPLY_TEMPLATE_TO_CLASS,
    handleApplyTemplateToClassroomSaga
  );
  yield takeLatest(
    courseTemplatesTypes.HANDLE_FETCH_SELECTED_COURSE_TEMPLATE,
    handleFetchSelectedCourseTemplateSaga
  );
  yield takeLatest(
    courseTemplatesTypes.HANDLE_DELETE_COURSE_TEMPLATES,
    handleDeleteCourseTemplatesSaga
  );
  yield takeLatest(
    courseTemplatesTypes.HANDLE_CREATE_COURSE_TEMPLATE,
    handleCreateCourseTemplateSaga
  );
  yield takeLatest(
    courseTemplatesTypes.HANDLE_UPDATE_COURSE_TEMPLATE,
    handleUpdateCourseTemplateSaga
  );
}
